// d:\projetos\www\clinica\admin-clinica\src\pages\onboarding\OnboardingServicesPage.js
import '../../styles/OnboardingExperience.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useEntidadeContext } from '../../EntidadeContext';
import { Button, Card, Modal, Form, Spinner, Table, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import CurrencyInput from 'react-currency-input-field';

const OnboardingServicesPage = () => {
    const navigate = useNavigate();
    const { completeOnboardingStep } = useAuthContext();
    const { currentEntidade } = useEntidadeContext();

    const [showServicoModal, setShowServicoModal] = useState(false);
    const [isLoadingServico, setIsLoadingServico] = useState(false);
    const [servicoSubmitMessage, setServicoSubmitMessage] = useState(null);
    const [novoServicoData, setNovoServicoData] = useState({
        nome: '',
        descricao: '',
        valor: 0,
        numero_sessoes: 1,
    });
    const [servicosCadastrados, setServicosCadastrados] = useState([]);
    const [tiposMateriaisDisponiveis, setTiposMateriaisDisponiveis] = useState([]);
    const [materiaisDisponiveis, setMateriaisDisponiveis] = useState([]);
    const [selectedMaterialsData, setSelectedMaterialsData] = useState([]); // [{ material_id: number, quantidade_utilizada: number, valor_unidade: number }]
    const [custoMaterialCalculado, setCustoMaterialCalculado] = useState(0);

    // Estados para o modal de adicionar material
    const [showMaterialModal, setShowMaterialModal] = useState(false);
    const [isLoadingMaterial, setIsLoadingMaterial] = useState(false);
    const [materialSubmitMessage, setMaterialSubmitMessage] = useState(null);
    const [novoMaterial, setNovoMaterial] = useState({
        nome: '',
        descricao: '',
        quantidade_adquirida: 0,
        valor: 0,
        valor_unidade: 0,
        unidade_medida: '',
        // Adicionando campos opcionais para evitar erros de "undefined key" no backend
        fornecedor: '',
        lote: '',
        validade: '',
        fabricante: '',
        numero_serie: '',
    });

    // Agrupa os materiais por tipo para exibição no modal
    const materiaisAgrupados = useMemo(() => {
        if (!materiaisDisponiveis) return {};
        return materiaisDisponiveis.reduce((acc, material) => {
            // Usa 'Sem Categoria' como fallback se o tipo não estiver definido
            const tipoNome = material.tipo_material?.nome || 'Sem Categoria';
            if (!acc[tipoNome]) {
                acc[tipoNome] = [];
            }
            acc[tipoNome].push(material);
            return acc;
        }, {});
    }, [materiaisDisponiveis]);

    const handleCompleteStep = async () => {
        try {
            const response = await completeOnboardingStep('services');
            if (response.onboarding_complete) {
                navigate('/dashboard');
            } else if (response.next_step_key === 'pending_collaborators') {
                navigate('/onboarding/collaborators');
            } else { // Fallback
                navigate('/dashboard');
            }
        } catch (error) {
            console.error("Erro ao completar passo (serviços):", error);
            alert(`Erro ao finalizar esta etapa: ${error.response?.data?.message || error.message}`);
        }
    };

    const fetchServicos = useCallback(async () => {
        if (currentEntidade?.id) {
            try {
                const response = await axios.get(`/api/servicos?entidade_id=${currentEntidade.id}`);
                setServicosCadastrados(response.data.servicos || []);
            } catch (error) {
                console.error("Erro ao buscar serviços:", error);
                setServicoSubmitMessage({ type: 'danger', text: 'Erro ao carregar serviços existentes.' });
            }
        }
    }, [currentEntidade]);

    const fetchTiposMateriais = useCallback(async () => {
        if (currentEntidade?.id) {
            try {
                const response = await axios.get(`/api/tipos_materiais?entidade_id=${currentEntidade.id}`);
                setTiposMateriaisDisponiveis(response.data.tipos_materiais || []);
            } catch (error) {
                console.error("Erro ao buscar tipos de materiais:", error);
                setServicoSubmitMessage({ type: 'danger', text: 'Erro ao carregar tipos de materiais.' });
            }
        }
    }, [currentEntidade]);

    const fetchMateriais = useCallback(async () => {
        if (currentEntidade?.id) {
            try {
                // Endpoint que retorna materiais com tipo e estoque (incluindo valor_unidade)
                const response = await axios.get(`/api/materiais-com-tipo?entidade_id=${currentEntidade.id}`);
                const materiaisDaApi = response.data.materiais || [];

                // Filtra para garantir que cada 'materiais_tipos_id' apareça apenas uma vez,
                // evitando a duplicação de materiais com fabricantes diferentes na listagem.
                const materiaisUnicos = [];
                const tiposVistos = new Set();
                for (const material of materiaisDaApi) {
                    if (!tiposVistos.has(material.materiais_tipos_id)) {
                        tiposVistos.add(material.materiais_tipos_id);
                        materiaisUnicos.push(material);
                    }
                }
                setMateriaisDisponiveis(materiaisUnicos);
            } catch (error) {
                console.error("Erro ao buscar materiais:", error);
                setServicoSubmitMessage({ type: 'danger', text: 'Erro ao carregar materiais.' });
            }
        }
    }, [currentEntidade]);

    useEffect(() => {
        fetchTiposMateriais();
        fetchMateriais();
        fetchServicos();
    }, [fetchTiposMateriais, fetchMateriais, fetchServicos]);

    useEffect(() => {
        let custoTotal = 0;
        selectedMaterialsData.forEach(item => {
            const materialOriginal = materiaisDisponiveis.find(m => m.id === item.material_id);
            if (materialOriginal && materialOriginal.valor_unidade) {
                custoTotal += item.quantidade_utilizada * parseFloat(materialOriginal.valor_unidade);
            }
        });
        setCustoMaterialCalculado(parseFloat(custoTotal.toFixed(2)));
    }, [selectedMaterialsData, materiaisDisponiveis]);


    const resetNovoServicoForm = () => {
        setNovoServicoData({
            nome: '',
            descricao: '',
            valor: 0,
            numero_sessoes: 1,
        });
        setSelectedMaterialsData([]);
        setCustoMaterialCalculado(0);
    };

    const resetNovoMaterialForm = () => {
        setNovoMaterial({
            nome: '',
            descricao: '',
            quantidade_adquirida: 0,
            valor: 0,
            valor_unidade: 0,
            unidade_medida: '',
            fornecedor: '',
            lote: '',
            validade: '',
            fabricante: '',
            numero_serie: '',
        });
    };

    const handleOpenMaterialModal = () => {
        resetNovoMaterialForm();
        setMaterialSubmitMessage(null);
        setShowMaterialModal(true);
    };

    const handleCloseMaterialModal = () => setShowMaterialModal(false);

    const handleChangeNovoMaterial = (eventOrValue, nameAttr) => {
        const name = nameAttr || eventOrValue.target.name;
        const rawValue = nameAttr ? eventOrValue : eventOrValue.target.value;
 
        setNovoMaterial(prevState => {
            const newState = { ...prevState };
 
            if (name === 'quantidade_adquirida') {
                const intValue = parseInt(rawValue, 10);
                newState[name] = isNaN(intValue) ? 0 : intValue;
            } else {
                // Para 'valor' e outros campos, apenas armazena o valor bruto.
                // O CurrencyInput lida com a formatação. `rawValue` será undefined se o campo estiver vazio.
                newState[name] = rawValue;
            }
 
            // Para o cálculo, usamos os valores convertidos para float.
            const valor = parseFloat(String(newState.valor)) || 0;
            const quantidade = parseFloat(newState.quantidade_adquirida) || 0;
 
            if (quantidade > 0 && valor > 0) {
                newState.valor_unidade = (valor / quantidade).toFixed(2);
            } else {
                newState.valor_unidade = 0;
            }
            return newState;
        });
    };

    const handleSubmitNovoMaterial = async (event) => {
        event.preventDefault();
        setIsLoadingMaterial(true);
        setMaterialSubmitMessage(null);
        if (!currentEntidade?.id) {
            setMaterialSubmitMessage({ type: 'danger', text: 'Entidade não identificada.' });
            setIsLoadingMaterial(false);
            return;
        }

        const dataPayload = {
            ...novoMaterial,
            // Garante que o valor seja enviado como número (float) para a API
            valor: parseFloat(String(novoMaterial.valor).replace(',', '.')) || 0,
            entidade_id: currentEntidade.id,
            quantidade_em_estoque: novoMaterial.quantidade_adquirida
        };

        try {
            await axios.post(`/api/materiais/criar-com-tipo`, dataPayload);
            setMaterialSubmitMessage({ type: 'success', text: `Material "${novoMaterial.nome}" adicionado com sucesso! A lista será atualizada.` });
            resetNovoMaterialForm();
            fetchMateriais(); // Re-busca os materiais para atualizar a lista no modal de serviço
            setTimeout(() => handleCloseMaterialModal(), 2000); // Fecha o modal após um tempo para o usuário ver a mensagem
        } catch (error) {
            setMaterialSubmitMessage({ type: 'danger', text: error.response?.data?.message || 'Erro ao criar material.' });
        } finally {
            setIsLoadingMaterial(false);
        }
    };

    const handleOpenServicoModal = () => {
        resetNovoServicoForm();
        setServicoSubmitMessage(null);
        setShowServicoModal(true);
    };

    const handleCloseServicoModal = () => setShowServicoModal(false);

    const handleChangeNovoServico = (eventOrValue, nameAttr) => {
        const name = nameAttr || eventOrValue.target.name;
        const rawValue = nameAttr ? eventOrValue : eventOrValue.target.value;

        // Para o CurrencyInput, rawValue é a string não formatada (ex: "123.45") ou undefined.
        // Armazenamos diretamente para que o componente controle a digitação.
        setNovoServicoData(prev => ({ ...prev, [name]: rawValue }));
    };

    const handleMaterialChange = (materialId, isChecked) => {
        setSelectedMaterialsData(prev => {
            if (isChecked) {
                const material = materiaisDisponiveis.find(m => m.id === materialId);
                return [...prev, { material_id: materialId, quantidade_utilizada: 0, valor_unidade: material?.valor_unidade || 0 }];
            } else {
                return prev.filter(item => item.material_id !== materialId);
            }
        });
    };

    const handleQuantidadeChange = (materialId, quantidade) => {
        const parsedQty = parseInt(quantidade, 10);
        if (isNaN(parsedQty) || parsedQty < 0) return; // Ignora se não for um número válido ou negativo

        setSelectedMaterialsData(prev =>
            prev.map(item =>
                item.material_id === materialId ? { ...item, quantidade_utilizada: parsedQty } : item
            )
        );
    };

    const handleSubmitNovoServico = async (event) => {
        event.preventDefault();
        setIsLoadingServico(true);
        setServicoSubmitMessage(null);

        if (!currentEntidade?.id) {
            setServicoSubmitMessage({ type: 'danger', text: 'Entidade não identificada.' });
            setIsLoadingServico(false);
            return;
        }

        const payload = {
            nome: novoServicoData.nome,
            descricao: novoServicoData.descricao,
            valor: parseFloat(novoServicoData.valor) || 0,
            numero_sessoes: novoServicoData.numero_sessoes || 1,
            custo_material: custoMaterialCalculado,
            entidade_id: currentEntidade.id,
            materiais: selectedMaterialsData
                .filter(item => item.quantidade_utilizada > 0) // Enviar apenas materiais com quantidade > 0
                .map(item => ({
                    id: item.material_id,
                    quantidade_utilizada: item.quantidade_utilizada
                })),
        };

        try {
            const response = await axios.post('/api/servicos', payload);

            // A API retorna o serviço sem a lista de materiais.
            // Para atualizar a UI corretamente, vamos construir o objeto completo.
            const servicoDaApi = response.data.servico;

            // Busca os detalhes completos dos materiais que foram enviados no payload
            const materiaisDoServico = payload.materiais.map(materialPayload => {
                const materialCompleto = materiaisDisponiveis.find(m => m.id === materialPayload.id);
                return {
                    ...materialCompleto, // Inclui nome, tipo_material, etc.
                    pivot: { // Simula a estrutura de 'pivot' que a renderização espera
                        quantidade_utilizada: materialPayload.quantidade_utilizada
                    }
                };
            });

            // Combina o serviço da API com os detalhes dos materiais
            const servicoCompletoParaUi = {
                ...servicoDaApi,
                materiais: materiaisDoServico
            };

            setServicosCadastrados(prev => [...prev, servicoCompletoParaUi]);
            setServicoSubmitMessage({ type: 'success', text: `Serviço "${response.data.servico.nome}" adicionado com sucesso!` });
            setIsLoadingServico(false); // Para o spinner para mostrar a mensagem

            // Agenda o fechamento do modal para que o usuário veja a mensagem e a animação
            setTimeout(() => {
                handleCloseServicoModal();
            }, 1000); // Fecha após 1.5 segundos
        } catch (error) {
            console.error("Erro ao criar serviço:", error.response?.data || error.message);
            setServicoSubmitMessage({ type: 'danger', text: error.response?.data?.message || 'Erro ao criar serviço.' });
            setIsLoadingServico(false); // Para o spinner no erro também
        }
    };


    return (
        <Card className="onboarding-shell onboarding-shell--services">
            <Card.Body>
                <div className="onboarding-hero">
                    <div className="onboarding-progress"><span>Etapa 2 de 3</span><div className="onboarding-progress__track"><div className="onboarding-progress__bar" style={{ width: '66%' }} /></div></div>
                    <div className="onboarding-hero__row"><div className="onboarding-hero__icon">✨</div><div><span className="onboarding-hero__eyebrow">Dê forma ao seu atendimento</span><h1>Monte seu catálogo de serviços</h1><p>Defina preços, sessões e materiais de um jeito simples e visual.</p></div></div>
                </div>

                <Alert variant="info" className="onboarding-tip">
                    <Alert.Heading>Qual a importância?</Alert.Heading>
                    <p>Cadastrar seus serviços permite associar custos de materiais, definir preços e agilizar o processo de agendamento e faturamento. É um passo crucial para a organização do seu negócio.</p>
                </Alert>

                <div className="d-grid gap-2 mb-4">
                    <Button className="onboarding-primary" variant="primary" size="lg" onClick={handleOpenServicoModal}>
                        Adicionar Novo Serviço
                    </Button>
                </div>

                {servicosCadastrados.length === 0 && (
                    <Alert variant="light" className="onboarding-preview mt-4">
                        <Alert.Heading as="h6" className="text-muted">Exemplo de como ficará seu serviço</Alert.Heading>
                        <Table bordered size="sm" className="onboarding-table mb-0 mt-2 bg-white">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Valor (R$)</th>
                                    <th>Custo Material (R$)</th>
                                    <th>Materiais Utilizados</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Limpeza de Pele Profunda</td>
                                    <td>150,00</td>
                                    <td>15,50</td>
                                    <td>
                                        <ul className="list-unstyled mb-0 small text-start ps-2">
                                            <li>- Máscara Calmante (1 unid.)</li>
                                            <li>- Algodão (5 Grama)</li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Alert>
                )}

                {servicosCadastrados.length > 0 && (
                    <>
                        <h5 className="onboarding-section-title">Serviços Adicionados:</h5>
                        <Table striped bordered hover responsive size="sm" className="onboarding-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Valor (R$)</th>
                                    <th>Custo Material (R$)</th>
                                    <th>Materiais Utilizados</th>
                                </tr>
                            </thead>
                            <tbody>
                                {servicosCadastrados.map((servico) => (
                                    <tr key={servico.id}>
                                        <td>{servico.nome}</td>
                                        <td>{parseFloat(servico.valor).toFixed(2)}</td>
                                        <td>{parseFloat(servico.custo_material).toFixed(2)}</td>
                                        <td>
                                            {servico.materiais && servico.materiais.length > 0 ? (
                                                <ul className="list-unstyled mb-0">
                                                    {servico.materiais.map((material) => (
                                                        <li key={material.id}>
                                                            {material.nome} ({material.pivot.quantidade_utilizada} {material.tipo_material?.unidade_medida || 'unid.'})
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : ('Nenhum')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}

                <Modal className="onboarding-modal" centered show={showServicoModal} onHide={handleCloseServicoModal} size="lg" animation={true}>
                    <Modal.Header closeButton>
                        <Modal.Title>Adicionar Novo Serviço</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {servicoSubmitMessage && (
                            <Alert variant={servicoSubmitMessage.type} onClose={() => setServicoSubmitMessage(null)} dismissible>
                                {servicoSubmitMessage.text}
                            </Alert>
                        )}
                        <Form onSubmit={handleSubmitNovoServico}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="onboardingServicoNome"><Form.Label>Nome do Serviço *</Form.Label><Form.Control type="text" name="nome" value={novoServicoData.nome} onChange={handleChangeNovoServico} required /></Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="onboardingServicoSessoes">
                                        <Form.Label>Nº de Sessões *</Form.Label>
                                        <Form.Control type="number" name="numero_sessoes" value={novoServicoData.numero_sessoes} onChange={handleChangeNovoServico} min="1" required />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3" controlId="onboardingServicoValor"><Form.Label>Valor do Serviço (R$) *</Form.Label><CurrencyInput id="onboardingServicoValorInput" name="valor" className="form-control" placeholder="R$ 0,00" value={novoServicoData.valor} decimalsLimit={2} decimalSeparator="," groupSeparator="." onValueChange={(value) => handleChangeNovoServico(value, 'valor')} required /></Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3" controlId="onboardingServicoDescricao"><Form.Label>Descrição</Form.Label><Form.Control as="textarea" rows={2} name="descricao" value={novoServicoData.descricao} onChange={handleChangeNovoServico} /></Form.Group>
                            
                            <hr />
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5>Materiais Utilizados</h5>
                                <Button variant="outline-success" size="sm" onClick={handleOpenMaterialModal}>
                                    + Adicionar Material
                                </Button>
                            </div>  
                            <p className="text-muted">Selecione os materiais e as quantidades usadas para realizar este serviço. O custo será calculado automaticamente.</p>

                            {Object.keys(materiaisAgrupados).length > 0 ? (
                                Object.entries(materiaisAgrupados).map(([tipoNome, materiaisDoTipo]) => (
                                    <div key={tipoNome} className="mb-3">
                                        <h6>{tipoNome}</h6>
                                        {materiaisDoTipo.map(material => (
                                            <Row key={material.id} className="mb-2 p-2 border rounded align-items-center gx-2">
                                                <Col xs={12} md={6}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`material-check-${material.id}`}
                                                        label={`${material.nome} (${material.fabricante || 'sem fabricante cadastrado'})`}
                                                        checked={selectedMaterialsData.some(item => item.material_id === material.id)}
                                                        onChange={(e) => handleMaterialChange(material.id, e.target.checked)}
                                                    />
                                                    <small className="text-muted d-block ms-4">Estoque: {material.tipo_material?.estoque?.quantidade_em_estoque || 0} {material.tipo_material?.unidade_medida || 'unid.'}</small>
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    {selectedMaterialsData.some(item => item.material_id === material.id) && (
                                                        <InputGroup size="sm">
                                                            <Form.Control className="no-spinner" type="number" placeholder="Qtd." min="0" max={material.tipo_material?.estoque?.quantidade_em_estoque || 0} value={selectedMaterialsData.find(item => item.material_id === material.id)?.quantidade_utilizada || ''} onChange={(e) => handleQuantidadeChange(material.id, e.target.value)} />
                                                            <InputGroup.Text>{material.tipo_material?.unidade_medida || 'unid.'}</InputGroup.Text>
                                                        </InputGroup>
                                                    )}
                                                </Col>
                                            </Row>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <Alert variant="warning">Nenhum material cadastrado no estoque. <a href="/onboarding/materials">Cadastre um material</a> para poder associá-lo a um serviço.</Alert>
                            )}

                            <div className="text-end mt-3">
                                <strong>Custo Total dos Materiais: R$ {custoMaterialCalculado.toFixed(2)}</strong>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <Button variant="secondary" onClick={handleCloseServicoModal} className="me-2" disabled={isLoadingServico}>Fechar Janela</Button>
                                <Button variant="primary" type="submit" disabled={isLoadingServico}>{isLoadingServico ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar Serviço'}</Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>

                {/* Modal para Adicionar Novo Material */}
                <Modal className="onboarding-modal modal-material-quick-add" show={showMaterialModal} onHide={handleCloseMaterialModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Adicionar Novo Material ao Estoque</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {materialSubmitMessage && (
                            <Alert variant={materialSubmitMessage.type} onClose={() => setMaterialSubmitMessage(null)} dismissible>
                                {materialSubmitMessage.text}
                            </Alert>
                        )}
                        <Form onSubmit={handleSubmitNovoMaterial}>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3" controlId="onboardingMaterialNome"><Form.Label>Nome do Material *</Form.Label><Form.Control type="text" name="nome" value={novoMaterial.nome} onChange={handleChangeNovoMaterial} required /></Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3" controlId="onboardingMaterialUnidade"><Form.Label>Unidade *</Form.Label><Form.Select name="unidade_medida" value={novoMaterial.unidade_medida} onChange={handleChangeNovoMaterial} required><option value="">Selecione...</option><option value="Unidade">Unidade</option><option value="Grama">Grama</option><option value="Ml">Ml</option></Form.Select></Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3" controlId="onboardingMaterialDescricao"><Form.Label>Descrição</Form.Label><Form.Control as="textarea" rows={2} name="descricao" value={novoMaterial.descricao} onChange={handleChangeNovoMaterial} /></Form.Group>
                            <Form.Group className="mb-3" controlId="onboardingMaterialFabricante">
                                <Form.Label>Fabricante</Form.Label><Form.Control type="text" name="fabricante" value={novoMaterial.fabricante} onChange={handleChangeNovoMaterial} />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="onboardingMaterialQtdAdquirida"><Form.Label>Quantidade Adquirida *</Form.Label><Form.Control className="no-spinner" type="number" name="quantidade_adquirida" value={novoMaterial.quantidade_adquirida || ''} onChange={handleChangeNovoMaterial} min="0" required /></Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="onboardingMaterialValorTotal"><Form.Label>Valor Total (R$) *</Form.Label><CurrencyInput id="onboardingMaterialValorTotalInput" name="valor" className="form-control" placeholder="R$ 0,00" value={novoMaterial.valor} decimalsLimit={2} decimalSeparator="," groupSeparator="." onValueChange={(value) => handleChangeNovoMaterial(value, 'valor')} required /></Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={{ span: 6, offset: 6 }}>
                                    <Form.Group className="mb-3" controlId="onboardingMaterialValorUnidade">
                                        <Form.Label>Valor por Unidade</Form.Label>
                                        <InputGroup><InputGroup.Text>R$</InputGroup.Text><Form.Control type="text" name="valor_unidade" value={novoMaterial.valor_unidade} readOnly className="bg-light" /></InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex justify-content-end">
                                <Button variant="outline-secondary" onClick={handleCloseMaterialModal} className="me-2" disabled={isLoadingMaterial}>
                                    Fechar
                                </Button>
                                <Button variant="success" type="submit" disabled={isLoadingMaterial}>
                                    {isLoadingMaterial ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar Material'}
                                </Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>

                <Button variant="success" onClick={handleCompleteStep} className="onboarding-complete" disabled={servicosCadastrados.length === 0}>
                    Concluí Cadastro de Serviços e Ir para Médicos

                </Button>
            </Card.Body>
        </Card>
    );
};

export default OnboardingServicesPage;
